import { Component, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormControl,
	UntypedFormGroup,
	Validators,
	ReactiveFormsModule
} from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StorageKey } from 'src/app/_models/local-storage-key';
import { AuthenticationV2Service } from 'src/app/authentication-v2/services/authentication-v2.service';
import { CustomValidator } from 'src/app/shared/form-input/input-password/custom-validator';
import { allCountries } from 'src/app/shared/storage/countries-data-store';
import { BackOfficeService } from '../../../../_services/back-office/back-office.service';
import { SavePasswordRequestModel } from './model/profile.model';
import {
	LoButton,
	LoCard,
	LoIcon,
	LoSkeleton,
	LoTextField,
	LoToastService
} from 'lo-design-system';
import { TeamPermissionsStateService } from 'src/app/user-management/team/services/team-permissions-state.service';
import { UserRoleEnum } from '../../../../shared-components/models/user-role.model';
import { AnaSettingsRevealService } from '@brand-lolly/app/_services/ana/ana-settings-reveal.service';
import { LandingPageService } from 'src/app/services/landing-page.service';
import { SharedService } from 'src/app/shared/services/shared.service';
import {
	AccountBrandsComponent,
	AccountProfile
} from '../account-brands/account-brands.component';

export interface Countries {
	code: string;
	code3: string;
	name: string;
	number: string;
	phone: string;
}

@Component({
	standalone: true,
	selector: 'app-my-profile',
	templateUrl: './new-my-profile.component.html',
	styleUrls: ['./my-profile.component.scss'],
	imports: [
		ReactiveFormsModule,
		LoButton,
		LoCard,
		LoIcon,
		LoSkeleton,
		LoTextField,
		AccountBrandsComponent
	]
})
export class MyProfileComponent implements OnInit, OnDestroy {
	public userForm: UntypedFormGroup;
	public contactForm: UntypedFormGroup;
	public securityForm: UntypedFormGroup;
	private unsubscriber$: Subject<void> = new Subject<void>();
	private settingsScope = `settings:my-profile:${Math.random()
		.toString(36)
		.slice(2)}`;
	public isEditActive2: boolean = false;
	public isEditActive3: boolean = false;

	public countries: Countries[] = allCountries;
	public allCountryCodes: string[] = this.countries.map(
		c => c.name + ' +' + c.phone
	);

	public accessLevel = [
		{ label: 'Standard', value: 'Standard' },
		{ label: 'View only', value: 'View only' }
	];

	public loading: boolean = false;
	/** The account's name, site and logo — the Brands card shows them on the account brand. */
	public accountProfile: AccountProfile | null = null;
	/** A read-only seat may not change its password; the edit entry is disabled for it. */
	public readOnlyRole: boolean = false;

	/** The password rules the new password is checked against, by the validator error each one raises. */
	public readonly passwordRules = [
		{
			error: 'minlength',
			label: 'Password must be at least 8 characters long'
		},
		{ error: 'hasSmallCase', label: 'One lowercase character' },
		{ error: 'hasCapitalCase', label: 'One uppercase character' },
		{
			error: 'hasSpecialCharacters',
			label: 'One number or special character'
		}
	];
	/**
	 * Whether this person may change the ACCOUNT's brand identity — its name,
	 * its website and its logo.
	 *
	 * Those three are the account's, not the person's: a seat is created with
	 * the owner's company and website copied onto its row, so every member's
	 * profile shows the same brand. Letting a member edit them would let them
	 * rename the company for everybody from their own profile page.
	 *
	 * NOT `RoleBasedControlDirective`, which used to guard this control: it
	 * reads the JWT role claim, falls through to the owner branch when that
	 * claim is absent, and its two non-owner branches are unreachable because
	 * nothing binds its action type. The role matrix is the server's own answer
	 * and is fail-closed once it has been read.
	 */
	public canEditBrandIdentity: boolean = true;

	constructor(
		public backOfficeService: BackOfficeService,
		private formBuilder: UntypedFormBuilder,
		private toasts: LoToastService,
		private authV2Service: AuthenticationV2Service,
		private permissionsState: TeamPermissionsStateService,
		private settingsReveal: AnaSettingsRevealService,
		private landingPageService: LandingPageService,
		private sharedService: SharedService
	) {}

	public ngOnInit(): void {
		this.settingsReveal.registerPage(this.settingsScope, {
			keys: ['profile.company_name', 'profile.website'],
			// TRAP: a no-op on purpose — both keys are find-only, and this page's
			// re-read (fetchStore) would discard an edit the brand has open.
			reload: () => undefined
		});
		this.readOnlyRole =
			this.authV2Service.getUserRole() === UserRoleEnum.READ_ONLY;
		this.createForm();
		this.fetchStore();
		this.permissionsState
			.ensureLoaded()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(() => {
				this.canEditBrandIdentity = this.permissionsState.isOwner;
			});
	}

	public ngOnDestroy(): void {
		this.settingsReveal.unregisterPage(this.settingsScope);
		this.unsubscriber$.next();
		this.unsubscriber$.complete();
	}

	public editName3(): void {
		this.isEditActive3 = !this.isEditActive3;
		this.securityForm.reset();
	}

	public logout(url): void {
		// this.profileOpen = false;
		// this.authenticationService.expandMenu$.next(false);
		this.authV2Service.signoutUser(url);
	}

	private warn(message: string): void {
		this.toasts.warning(message);
	}

	private onError(err): void {
		const title = 'Oops, something went wrong!';
		const msg = 'Please try again!';

		this.toasts.error(msg, { heading: title, duration: 2000 });
	}

	private fetchStore(): void {
		this.loading = true;
		this.backOfficeService
			.getProfileDetails(localStorage.getItem(StorageKey.token))
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				res => {
					this.accountProfile = {
						company: res?.company ?? null,
						website: res?.company_website ?? null,
						logo: res?.company_logo ?? null
					};
					this.userForm.patchValue({
						firstname: res?.first_name,
						lastname: res?.last_name,
						accessLevel: res?.position,
						is_google_auth: res?.is_google_auth
					});
					this.contactForm.patchValue({
						current_phone: res?.phone_number
							?.split(' ')
							?.slice(1)
							?.join(' '),
						current_email: res?.email,
						phone: res?.phone_number
							?.split(' ')
							?.slice(1)
							?.join(' '),
						email: res?.email,
						phone_code: res?.phone_number?.split(' ')[0],
						current_phone_code: res?.phone_number?.split(' ')[0]
					});
				},
				err => this.onError
			)
			.add(() => (this.loading = false));
	}

	private createForm(): void {
		this.userForm = this.formBuilder.group({
			firstname: new UntypedFormControl('', Validators.required),
			lastname: new UntypedFormControl('', Validators.required),
			accessLevel: new UntypedFormControl('', Validators.required),
			is_google_auth: new UntypedFormControl(true)
		});

		this.contactForm = this.formBuilder.group({
			// Current Value Controls
			current_email: new UntypedFormControl(null, [
				Validators.required,
				Validators.email,
				Validators.pattern(
					/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
				)
			]),
			current_phone: new UntypedFormControl(null, [
				Validators.minLength(8)
			]),
			current_phone_code: new UntypedFormControl(null),

			// Editable Value Controls
			email: new UntypedFormControl(null, [
				Validators.required,
				Validators.email,
				Validators.pattern(
					/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
				)
			]),
			phone: new UntypedFormControl(null, [Validators.minLength(8)]),
			phone_code: new UntypedFormControl(null)
		});

		this.securityForm = this.formBuilder.group(
			{
				current: new UntypedFormControl('', Validators.required),
				password: new UntypedFormControl('', [
					Validators.required,
					Validators.minLength(8),
					// check whether the entered password has a lower case letter
					CustomValidator.patternValidator(/[a-z]/, {
						hasSmallCase: true
					}),
					// check whether the entered password has upper case letter
					CustomValidator.patternValidator(/[A-Z]/, {
						hasCapitalCase: true
					}),
					// CustomValidator.patternValidator(/\d/, {
					// 	hasNumber: true
					// }),
					// // check whether the entered password has a special character
					// CustomValidator.patternValidator(/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/, {
					// 	hasSpecialCharacters: true
					// }),
					// check whether the entered password has a number or special character
					CustomValidator.patternValidator(
						/\d|[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/,
						{
							hasSpecialCharacters: true
						}
					)
				]),
				confirmPassword: new UntypedFormControl('', [
					Validators.required
				])
			},
			{
				// check whether our password and confirm password match
				validator: CustomValidator.passwordMatchValidator
			}
		);
	}

	public dropDownValueChanges(selected: any): void {
		if (selected) {
			this.userForm.get('accessLevel').setValue(selected);
		}
	}

	public editDetails(cb?): void {
		const payload: any = {};
		const formData = new FormData();
		let phoneNumber = `${this.contactForm.get('phone_code').value} ${
			this.contactForm.get('phone').value
		}`;

		// if (this.contactForm.get('email').dirty) {
		// 	if (!this.contactForm.get('email').valid) return this.toastNotification.sendWarningToast('Please enter a proper Email');
		// 	payload.email = this.contactForm.get('email').value;
		// }

		if (!this.contactForm.get('phone').valid)
			return this.warn('Please enter a proper Phone Number');
		payload.phone_number = phoneNumber;

		// if (this.userForm.get('firstname').dirty) {
		// 	if (!this.userForm.get('firstname').valid) return this.toastNotification.sendWarningToast('Please enter a valid Name');
		// 	payload.first_name = this.userForm.get('firstname').value;
		// }

		// if (this.userForm.get('lastname').dirty) {
		// 	if (!this.userForm.get('lastname').valid) return this.toastNotification.sendWarningToast('Please enter a valid Last Name');
		// 	payload.last_name = this.userForm.get('lastname').value;
		// }

		for (let key in payload) formData.append(key, payload[key]);

		if (Object.keys(payload).length === 0) {
			this.warn('Please edit the details to Save');
			return;
		}

		this.backOfficeService
			.updateProfileDetails(formData)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(res => {
				if (res.status) {
					// this.toastNotification.sendSuccessToast('New Details were Updated');
					this.updateToken();

					if (this.isEditActive2) {
						this.isEditActive2 = false;
						this.updateCurrentAddressControls();
					}
					if (cb) cb();
				}
			});
	}

	public updateCurrentAddressControls(): void {
		const newEmail = this.contactForm.get('email').value;
		const newPhone = this.contactForm.get('phone').value;
		const newPhoneCode = this.contactForm.get('phone_code').value;
		this.contactForm.get('current_email').patchValue(newEmail);
		this.contactForm.get('current_phone').patchValue(newPhone);
		this.contactForm.get('current_phone_code').patchValue(newPhoneCode);
	}

	private updateToken(): void {
		this.authV2Service.updateAccessTokenFromServer();
	}

	/** The Brands card saved the account brand: re-read the account's name, site and logo. */
	public onAccountBrandSaved(): void {
		// CONTRACT: the access token carries company_name, minted from users.company on refresh.
		this.updateToken();
		this.fetchStore();
		this.refreshSidenavProfile();
	}

	/** Another brand was saved, made default or removed: the header shows the default brand. */
	public onBrandsChanged(): void {
		this.refreshSidenavProfile();
	}

	/** The sidenav avatar and menu name are the default brand's, read once when the shell loads. */
	private refreshSidenavProfile(): void {
		this.landingPageService
			.getAllData()
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(res =>
				this.sharedService.userProfileData$.next(res?.data)
			);
	}

	public saveAddressDetails(): void {
		this.editDetails(() => {
			this.toasts.success('Your phone number is successfully changed!');
		});
	}

	public savePassword(): void {
		const { current, password } = this.securityForm.value;
		const payload: SavePasswordRequestModel = {
			old_password: current,
			new_password: password
		};

		this.backOfficeService
			.updatePassword(payload)
			.pipe(takeUntil(this.unsubscriber$))
			.subscribe(
				res => {
					this.toasts.success('Password updated successfully!');
					if (this.isEditActive3) this.isEditActive3 = false;
				},
				err => {
					if (err.status === 400) {
						const title = 'Oops, something went wrong!';
						const msg = err.error.detail;
						this.toasts.error(msg, {
							heading: title,
							duration: 2000
						});
					} else this.onError(err);
				}
			);
	}

	public toggleEdit2(): void {
		this.isEditActive2 = !this.isEditActive2;
		this.resetEditableAddressControls();
	}

	public resetEditableAddressControls(): void {
		const currentEmail = this.contactForm.get('current_email').value;
		const currentPhone = this.contactForm.get('current_phone').value;
		const currentPhoneCode =
			this.contactForm.get('current_phone_code').value;
		this.contactForm.get('email').patchValue(currentEmail);
		this.contactForm.get('email').markAsPristine();
		this.contactForm.get('phone').patchValue(currentPhone);
		this.contactForm.get('phone').markAsPristine();
		this.contactForm.get('phone_code').patchValue(currentPhoneCode);
		this.contactForm.get('phone_code').markAsPristine();
	}
}
